import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { authHeaders, fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSelectField,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const AccountStatementReport = (props) => {
  const { headId } = useParams();
  const [meta, setMeta] = useState({ categories: [] });
  const [accounts, setAccounts] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const metaUrl = headId
      ? `${API_URL}/account_statements/meta/${headId}`
      : `${API_URL}/account_statements/meta`;

    fetchReportJson(metaUrl)
      .then((data) => {
        setMeta(data);
        if (data.selected_category_id) {
          setInputValues((prev) => ({
            ...prev,
            account_category: String(data.selected_category_id),
            sub_category: data.selected_account_id ? String(data.selected_account_id) : '',
          }));
          loadAccounts(String(data.selected_category_id), data.selected_account_id);
        }
      })
      .catch((error) => swal('Statement of Account', error.message, 'error'));
  }, [headId]);

  const loadAccounts = async (categoryId, selectedAccountId = '') => {
    if (!categoryId) {
      setAccounts([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/account_statements/subcategory`, {
        method: 'post',
        headers: authHeaders(),
        body: JSON.stringify({
          value: categoryId,
          selected: selectedAccountId,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status === false) {
        throw new Error(data.message || 'Unable to load account names.');
      }
      setAccounts(data.accounts || []);
    } catch (error) {
      swal('Statement of Account', error.message, 'error');
    }
  };

  const onCategoryChange = (categoryId) => {
    setInputValues({ ...inputValues, account_category: categoryId, sub_category: '' });
    loadAccounts(categoryId);
  };

  const loadReport = async () => {
    if (!inputValues.start_date || !inputValues.end_date || !inputValues.account_category || !inputValues.sub_category) {
      swal('Please select category, account, and date range', '', 'warning');
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      const data = await fetchReportJson(`${API_URL}/account_statements`, {
        from_date: inputValues.start_date,
        to_date: inputValues.end_date,
        account_category: inputValues.account_category,
        sub_category: inputValues.sub_category,
      });
      setReport(data);
    } catch (error) {
      swal('Statement of Account', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    account_category: inputValues.account_category,
    sub_category: inputValues.sub_category,
  });

  const downloadPdf = async () => {
    if (!inputValues.start_date || !inputValues.end_date || !inputValues.account_category || !inputValues.sub_category) {
      swal('Please select category, account, and date range', '', 'warning');
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/account_statements/pdf`,
        buildPayload(),
        'account_statement.pdf',
      );
    } catch (error) {
      swal('Statement of Account', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const openingRows = (report?.opening_bal || []).filter((row) => Number(row.outstanding || 0) !== 0);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={4} op={4} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader
              title="Statement of Account"
              subtitle="Ledger activity for a selected account"
              badge="Reports"
            />

            <OtherReportFilterCard
              title="Report parameters"
              description="Choose the account and date range, then run the report."
            >
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <OtherReportSelectField
                      label="Account category"
                      value={inputValues.account_category}
                      placeholder="Select category"
                      span={3}
                      onChange={onCategoryChange}
                    >
                      {(meta.categories || []).map((item) => (
                        <option key={item.id} value={item.id}>{item.accounts_head}</option>
                      ))}
                    </OtherReportSelectField>
                    <OtherReportSelectField
                      label="Account name"
                      value={inputValues.sub_category}
                      placeholder="Select account"
                      span={3}
                      onChange={(value) => setInputValues({ ...inputValues, sub_category: value })}
                    >
                      {accounts.map((item) => (
                        <option key={item.id} value={item.id}>{item.accounts_head}</option>
                      ))}
                    </OtherReportSelectField>
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
            </OtherReportFilterCard>

            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating statement" />}

              {report && !loading && (
                <div className="form-wrap balace-sheetwrap">
                  <div className="row mb-3">
                    <div className="col-md">
                      <h2 className="table-title mb-1">{report.pump_name}</h2>
                      <h4 className="table-title mb-1">Statement of Account</h4>
                      <h5>{report.account_name}</h5>
                      <p>{formatReportDateRange(report.start_date, report.end_date)}</p>
                      <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                    </div>
                  </div>
                  <table className="table border">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Particulars</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openingRows.map((row, index) => (
                        <tr key={`opening-${index}`} className="table-light">
                          <td>{formatReportDate(report.start_date)}</td>
                          <td>{row.particulars || 'Opening balance'}</td>
                          <td />
                          <td className="report-amount">{reportNumberFormat(row.dr_amount)}</td>
                          <td className="report-amount">{reportNumberFormat(row.cr_amount)}</td>
                          <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                        </tr>
                      ))}
                      {(report.rows || []).map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td>{formatReportDate(row.ac_date)}</td>
                          <td>{row.particulars || ''}</td>
                          <td>{row.description || ''}</td>
                          <td className="report-amount">{reportNumberFormat(row.dr_amount)}</td>
                          <td className="report-amount">{reportNumberFormat(row.cr_amount)}</td>
                          <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                        </tr>
                      ))}
                      <tr className="fw-bold">
                        <td colSpan="3">Total / Closing balance</td>
                        <td className="report-amount">{reportNumberFormat(report.total_dr)}</td>
                        <td className="report-amount">{reportNumberFormat(report.total_cr)}</td>
                        <td className="report-amount">{reportNumberFormat(report.closing_balance)}{report.cr_dr}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default AccountStatementReport;
